//TokenHandler.js
import { useEffect, useRef } from "react";
import { Hub } from "@aws-amplify/core"; // Fixed import
import { fetchAuthSession } from "aws-amplify/auth";
import { useDispatch, useSelector } from "react-redux";
import { setAuthenticatedUser } from "./redux/slices/user";
import { AUTHENTICATED, UNAUTHENTICATED } from "./constants";
import { signOut } from "aws-amplify/auth";

const TokenHandler = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const refreshTimerRef = useRef(null);

  const handleSignOut = () => {
    // Clear all local storage items
    localStorage.removeItem("token");
    localStorage.removeItem("userProfileId");
    localStorage.removeItem("loggedInUserGroups");
    localStorage.removeItem("attributes");

    // Clear any refresh timers
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Sign out from Cognito
    signOut();
    
    // Update Redux state
    dispatch(
      setAuthenticatedUser({
        authState: UNAUTHENTICATED,
        role: "",
        attributes: {},
      })
    );
  };

  const refreshToken = async () => {
    try {
      console.log("Attempting to refresh token");
      const { tokens: session } = await fetchAuthSession({ forceRefresh: true });
      
      if (session?.accessToken) {
        // Update the stored token
        localStorage.setItem("token", JSON.stringify(session));
        console.log("Token refreshed successfully");
        
        // Schedule the next refresh
        scheduleTokenRefresh(session);
      } else {
        console.error("Failed to refresh token - no access token returned");
        handleSignOut();
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      handleSignOut();
    }
  };

  const scheduleTokenRefresh = (session) => {
    if (!session?.accessToken?.payload?.exp) {
      console.error("No expiration found in token");
      return;
    }

    // Calculate when to refresh the token (5 minutes before expiration)
    const expirationTime = session.accessToken.payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    const refreshBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
    const refreshTime = Math.max(timeUntilExpiration - refreshBuffer, 0);

    console.log(`Token expires in ${timeUntilExpiration / 60000} minutes. Will refresh in ${refreshTime / 60000} minutes`);

    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Set a new timer to refresh the token
    if (refreshTime > 0) {
      refreshTimerRef.current = setTimeout(refreshToken, refreshTime);
    } else {
      // If the token is already expired or very close to expiration, refresh now
      refreshToken();
    }
  };

  const checkTokenExpiration = () => {
    try {
      const tokenData = JSON.parse(localStorage.getItem("token"));
      
      if (!tokenData || !tokenData.accessToken || !tokenData.accessToken.payload) {
        console.log("No valid token found in localStorage");
        return false;
      }

      const expirationTime = tokenData.accessToken.payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      if (currentTime >= expirationTime) {
        console.log("Token expired based on expiration check");
        handleSignOut();
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error checking token expiration:", error);
      return false;
    }
  };

  useEffect(() => {
    // Only set up listeners if we're authenticated
    if (user?.authState !== AUTHENTICATED) {
      return;
    }

    console.log("Setting up token management");

    // Check if current token is valid and schedule refresh
    if (checkTokenExpiration()) {
      const tokenData = JSON.parse(localStorage.getItem("token"));
      scheduleTokenRefresh(tokenData);
    }

    // Listen for auth events using Amplify Hub
    const unsubscribe = Hub.listen("auth", ({ payload }) => {
      const { event } = payload;
      
      console.log("Auth event:", event);
      
      switch (event) {
        case "signIn":
          // A user has signed in, we'll get new tokens
          checkTokenExpiration();
          break;
          
        case "tokenRefresh":
          console.log("Token refreshed by Amplify");
          break;
          
        case "tokenRefresh_failure":
          console.error("Token refresh failed");
          handleSignOut();
          break;
          
        case "signOut":
          handleSignOut();
          break;
          
        case "tokenExpired":
          console.log("Token expired event received");
          handleSignOut();
          break;
      }
    });

    // Set up polling to periodically check token status
    const intervalId = setInterval(checkTokenExpiration, 60000); // Check every minute

    // Clean up on unmount
    return () => {
      console.log("Cleaning up token management");
      unsubscribe();
      clearInterval(intervalId);
      
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [user?.authState, dispatch]);

  return null; // This component doesn't render anything
};

export default TokenHandler;